/**
 * Bulk Claim Entry Page
 *
 * Allows rapid entry of multiple claims for members of the current organization.
 * Optimized for keyboard-driven data entry with auto-save persistence.
 *
 * Features:
 * - Keyboard navigation (Tab, Enter, Arrow keys)
 * - Auto-save on data entry
 * - Activity validation with dependency rules
 * - Bulk submission
 * - Organization auto-selection from org switcher
 */

import { useTabbedChrome } from '@/components/weavein';
import React, { useState, useEffect, useCallback, useRef, KeyboardEvent } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  billingDependenciesAPI,
  ValidationResult
} from '@/services/api/billingDependencies';
import {
  FileText,
  Plus,
  Trash2,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Save,
  Send,
  Copy,
  Keyboard,
  RefreshCw,
  ArrowLeft,
  Building2,
  Download,
  X,
} from 'lucide-react';

// Types
interface BulkClaimLineItem {
  id: string;
  member_id: number | null;
  member_name?: string;
  billing_activity_id: number | null;
  activity_name?: string;
  cpt_code?: string;
  hcpcs_code?: string;
  revenue_code?: string;
  modifier_1?: string;
  modifier_2?: string;
  service_date: string;
  place_of_service: string;
  units: number;
  charge_amount: number;
  diagnosis_code?: string;
  validation_status: 'pending' | 'valid' | 'warning' | 'error';
  validation_messages: string[];
  is_saved: boolean;
}

interface Member {
  id: number;
  first_name: string;
  last_name: string;
  member_number?: string;
}

interface BillingActivity {
  id: number;
  activity_name: string;
  activity_code?: string;
  cpt_code?: string;
  hcpcs_code?: string;
  revenue_code?: string;
  modifier_1?: string;
  modifier_2?: string;
  rate_per_unit: number;
  billing_unit: string;
}

interface ActivityDiagnosisCode {
  id: number;
  billing_activity_id: number;
  icd10_code_id: number;
  icd10_code: string;
  icd10_description: string;
  icd10_short_description?: string;
  is_default: boolean;
  display_order: number;
}

interface Payer {
  id: number;
  payer_name: string;
  payer_id_code?: string;
}

interface Program {
  id: number;
  name: string;
}

// Place of Service codes (CMS standard codes - fallback)
const PLACE_OF_SERVICE_CODES = [
  { value: '02', label: '02 - Telehealth' },
  { value: '03', label: '03 - School' },
  { value: '04', label: '04 - Homeless Shelter' },
  { value: '09', label: '09 - Prison/Correctional' },
  { value: '10', label: '10 - Telehealth (Home)' },
  { value: '11', label: '11 - Office' },
  { value: '12', label: '12 - Home' },
  { value: '13', label: '13 - Assisted Living' },
  { value: '14', label: '14 - Group Home' },
  { value: '15', label: '15 - Mobile Unit' },
  { value: '16', label: '16 - Temp Lodging' },
  { value: '17', label: '17 - Retail Clinic' },
  { value: '18', label: '18 - Worksite' },
  { value: '19', label: '19 - Off Campus OP' },
  { value: '20', label: '20 - Urgent Care' },
  { value: '21', label: '21 - Inpatient Hospital' },
  { value: '22', label: '22 - Outpatient Hospital' },
  { value: '23', label: '23 - Emergency Room' },
  { value: '24', label: '24 - ASC' },
  { value: '25', label: '25 - Birthing Center' },
  { value: '26', label: '26 - Military Facility' },
  { value: '31', label: '31 - SNF' },
  { value: '32', label: '32 - Nursing Facility' },
  { value: '33', label: '33 - Custodial Care' },
  { value: '34', label: '34 - Hospice' },
  { value: '49', label: '49 - Independent Clinic' },
  { value: '50', label: '50 - FQHC' },
  { value: '51', label: '51 - Psych Inpatient' },
  { value: '52', label: '52 - Psych Partial' },
  { value: '53', label: '53 - CMHC' },
  { value: '54', label: '54 - ICF' },
  { value: '55', label: '55 - Res Substance' },
  { value: '56', label: '56 - PRTC' },
  { value: '57', label: '57 - Non-Res Substance' },
  { value: '60', label: '60 - Mass Immunization' },
  { value: '62', label: '62 - Comp OP Rehab' },
  { value: '65', label: '65 - ESRD' },
  { value: '71', label: '71 - Public Health' },
  { value: '72', label: '72 - Rural Health' },
  { value: '81', label: '81 - Ind Lab' },
  { value: '99', label: '99 - Other' }
];

const generateId = () => `row-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
const STORAGE_KEY = 'bulk_claim_entry_draft';

interface BulkClaimEntryPageProps {
  onNavigate?: (tab: string) => void;
}

export default function BulkClaimEntryPage({ onNavigate }: BulkClaimEntryPageProps) {
  const tabbed = useTabbedChrome();
  const { t } = useAppTranslation();
  const gridRef = useRef<HTMLDivElement>(null);

  // State
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Organization from context
  const [organizationId, setOrganizationId] = useState<number | null>(null);
  const [organizationName, setOrganizationName] = useState<string>('');
  const [billingProviderNpi, setBillingProviderNpi] = useState<string>('');

  // Form data
  const [payerId, setPayerId] = useState<number | null>(null);
  const [programId, setProgramId] = useState<number | null>(null);

  // Reference data
  const [members, setMembers] = useState<Member[]>([]);
  const [payers, setPayers] = useState<Payer[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [billingActivities, setBillingActivities] = useState<BillingActivity[]>([]);
  const [programPlaceOfService, setProgramPlaceOfService] = useState<Array<{code: string; label: string; is_default: boolean}>>([]);
  const [activityDiagnosisCodes, setActivityDiagnosisCodes] = useState<Record<number, ActivityDiagnosisCode[]>>({});

  // Line items
  const [lineItems, setLineItems] = useState<BulkClaimLineItem[]>([]);
  const [focusedCell, setFocusedCell] = useState<{ rowIndex: number; field: string } | null>(null);

  // Keyboard shortcuts visibility
  const [showShortcuts, setShowShortcuts] = useState(false);

  // Master claims import
  const [showImportModal, setShowImportModal] = useState(false);
  const [importableClaims, setImportableClaims] = useState<any[]>([]);
  const [importLoading, setImportLoading] = useState(false);

  // Fetch organization details
  const fetchOrganization = async (cboId: string) => {
    try {
      const response = await fetch(getWeaveinApiUrl(`/api/cbos/${cboId}`), {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setOrganizationName(data.name || '');
      }
    } catch (err) {
      console.error('Error fetching organization:', err);
    }
  };

  // Initialize with organization from localStorage
  useEffect(() => {
    const cboId = localStorage.getItem('active_cbo_id');
    const cboName = localStorage.getItem('active_cbo_name');
    if (cboId) {
      setOrganizationId(parseInt(cboId));
      // If name is in localStorage, use it; otherwise fetch from API
      if (cboName) {
        setOrganizationName(cboName);
      } else {
        fetchOrganization(cboId);
      }
    }

    loadDraft();
    fetchPayers();
    fetchPrograms();
  }, []);

  // Fetch members when organization changes
  useEffect(() => {
    if (organizationId) {
      fetchMembers();
      fetchOrganizationNpi();
    }
  }, [organizationId]);

  // Fetch billing activities and POS codes when program changes
  useEffect(() => {
    if (programId) {
      fetchBillingActivities();
      fetchProgramPlaceOfService(programId);
    } else {
      setBillingActivities([]);
      setProgramPlaceOfService([]);
    }
  }, [programId]);

  // Auto-save when line items change
  useEffect(() => {
    if (lineItems.length > 0) {
      saveDraft();
    }
  }, [lineItems, payerId, programId]);

  // Initialize with one empty row if no items
  useEffect(() => {
    if (lineItems.length === 0) {
      addLineItem();
    }
  }, []);

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    const cboId = localStorage.getItem('active_cbo_id');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...(cboId && { 'X-CBO-ID': cboId })
    };
  };

  // Fetch functions
  const fetchMembers = async () => {
    try {
      const response = await fetch(
        getWeaveinApiUrl(`/api/members?page_size=1000${organizationId ? `&organization_id=${organizationId}` : ''}`),
        { credentials: 'include', headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        const membersList = data.members || data || [];
        setMembers(membersList.map((m: any) => ({
          id: m.id,
          first_name: m.first_name,
          last_name: m.last_name,
          member_number: m.member_number || m.member_id
        })));
      }
    } catch (err) {
      console.error('Error fetching members:', err);
    }
  };

  const fetchPayers = async () => {
    try {
      const response = await fetch(getWeaveinApiUrl('/api/claims/payers'), {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPayers(data);
      }
    } catch (err) {
      console.error('Error fetching payers:', err);
    }
  };

  const fetchPrograms = async () => {
    try {
      const response = await fetch(getWeaveinApiUrl('/api/programs'), {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPrograms(data);
      }
    } catch (err) {
      console.error('Error fetching programs:', err);
    }
  };

  const fetchBillingActivities = async () => {
    try {
      const response = await fetch(
        getWeaveinApiUrl(`/api/billing-activities?program_id=${programId}&is_active=true`),
        { credentials: 'include', headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setBillingActivities(data);
      }
    } catch (err) {
      console.error('Error fetching billing activities:', err);
    }
  };

  const fetchProgramPlaceOfService = async (pid: number) => {
    try {
      const response = await fetch(getWeaveinApiUrl(`/api/programs/${pid}/place-of-service`), {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        const posCodes = data
          .filter((pos: any) => pos.is_active)
          .sort((a: any, b: any) => a.display_order - b.display_order)
          .map((pos: any) => ({
            code: pos.place_of_service_code,
            label: `${pos.place_of_service_code} - ${pos.place_of_service_label}`,
            is_default: pos.is_default
          }));
        setProgramPlaceOfService(posCodes);
      } else {
        setProgramPlaceOfService([]);
      }
    } catch (err) {
      console.error('Error fetching program place of service:', err);
      setProgramPlaceOfService([]);
    }
  };

  const availablePlaceOfServiceCodes = programPlaceOfService.length > 0
    ? programPlaceOfService.map(pos => ({ value: pos.code, label: pos.label }))
    : PLACE_OF_SERVICE_CODES;

  const fetchActivityDiagnosisCodes = async (activityId: number) => {
    if (activityDiagnosisCodes[activityId]) {
      return activityDiagnosisCodes[activityId];
    }
    try {
      const response = await fetch(getWeaveinApiUrl(`/api/billing-activities/${activityId}/diagnosis-codes`), {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setActivityDiagnosisCodes(prev => ({
          ...prev,
          [activityId]: data
        }));
        return data;
      }
    } catch (err) {
      console.error('Error fetching activity diagnosis codes:', err);
    }
    return [];
  };

  const fetchOrganizationNpi = async () => {
    try {
      const response = await fetch(getWeaveinApiUrl(`/api/cbos/${organizationId}`), {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setBillingProviderNpi(data.npi || '');
      }
    } catch (err) {
      console.error('Error fetching organization:', err);
    }
  };

  // ---- Master Claims Import ----

  const fetchImportableMasterClaims = async () => {
    setImportLoading(true);
    try {
      const response = await fetch(
        getWeaveinApiUrl('/api/document-processing/master-claims?limit=50'),
        { credentials: 'include', headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        // Only show claims that haven't been converted to formal claims and have line items
        const available = (data.items || []).filter(
          (mc: any) => !mc.claim_id && ['draft', 'ready', 'approved'].includes(mc.status)
        );
        setImportableClaims(available);
      }
    } catch (err) {
      console.error('Error fetching master claims:', err);
    } finally {
      setImportLoading(false);
    }
  };

  const handleImportMasterClaim = async (masterClaimId: number) => {
    try {
      const response = await fetch(
        getWeaveinApiUrl(`/api/document-processing/master-claims/${masterClaimId}`),
        { credentials: 'include', headers: getAuthHeaders() }
      );
      if (!response.ok) {
        setError('Failed to load master claim details');
        return;
      }
      const mc = await response.json();
      const mcLineItems = mc.line_items || [];

      if (mcLineItems.length === 0) {
        setError('This master claim has no line items to import');
        return;
      }

      const defaultPos = programPlaceOfService.find(pos => pos.is_default)?.code || '12';

      // Create bulk entry rows from master claim line items
      const newRows: BulkClaimLineItem[] = mcLineItems.map((li: any, idx: number) => ({
        id: generateId(),
        member_id: mc.member_id,
        member_name: mc.member_name || undefined,
        billing_activity_id: null,
        service_date: li.service_date || '',
        place_of_service: defaultPos,
        units: 1,
        charge_amount: 0,
        diagnosis_code: '',
        validation_status: 'pending' as const,
        validation_messages: [],
        is_saved: false,
      }));

      // Append to existing line items (filter out empty placeholder rows first)
      setLineItems(prev => {
        const nonEmpty = prev.filter(item => item.member_id || item.service_date);
        return [...nonEmpty, ...newRows];
      });

      setShowImportModal(false);
      setSuccessMessage(`Imported ${newRows.length} line item(s) from master claim for ${mc.member_name || 'member #' + mc.member_id}`);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      console.error('Error importing master claim:', err);
      setError('Failed to import master claim');
    }
  };

  // Draft persistence
  const saveDraft = () => {
    const draft = {
      payerId,
      programId,
      billingProviderNpi,
      lineItems: lineItems.map(item => ({ ...item, is_saved: true })),
      savedAt: new Date().toISOString()
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
  };

  const loadDraft = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const draft = JSON.parse(saved);
        if (draft.lineItems && draft.lineItems.length > 0) {
          setPayerId(draft.payerId);
          setProgramId(draft.programId);
          setBillingProviderNpi(draft.billingProviderNpi || '');
          setLineItems(draft.lineItems);
          setSuccessMessage(`Restored ${draft.lineItems.length} items from draft (saved ${new Date(draft.savedAt).toLocaleString()})`);
          setTimeout(() => setSuccessMessage(null), 5000);
        }
      }
    } catch (err) {
      console.error('Error loading draft:', err);
    }
  };

  const clearDraft = () => {
    localStorage.removeItem(STORAGE_KEY);
    setLineItems([]);
    setSuccessMessage('Draft cleared');
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  // Line item management
  const createNewLineItem = (): BulkClaimLineItem => {
    const defaultPos = programPlaceOfService.find(pos => pos.is_default)?.code || '12';
    return {
      id: generateId(),
      member_id: null,
      billing_activity_id: null,
      service_date: new Date().toISOString().split('T')[0],
      place_of_service: defaultPos,
      units: 1,
      charge_amount: 0,
      validation_status: 'pending',
      validation_messages: [],
      is_saved: false
    };
  };

  const addLineItem = () => {
    const newItem = createNewLineItem();
    setLineItems(prev => [...prev, newItem]);
    setTimeout(() => {
      setFocusedCell({ rowIndex: lineItems.length, field: 'member_id' });
    }, 50);
  };

  const removeLineItem = (index: number) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const duplicateLineItem = (index: number) => {
    const item = lineItems[index];
    const newItem: BulkClaimLineItem = {
      ...item,
      id: generateId(),
      is_saved: false,
      validation_status: 'pending'
    };
    const newItems = [...lineItems];
    newItems.splice(index + 1, 0, newItem);
    setLineItems(newItems);
  };

  const updateLineItem = async (index: number, field: keyof BulkClaimLineItem, value: any) => {
    const updated = [...lineItems];
    updated[index] = {
      ...updated[index],
      [field]: value,
      is_saved: false
    };

    if (field === 'billing_activity_id' && value) {
      const activity = billingActivities.find(a => a.id === value);
      if (activity) {
        updated[index] = {
          ...updated[index],
          activity_name: activity.activity_name,
          cpt_code: activity.cpt_code,
          hcpcs_code: activity.hcpcs_code,
          revenue_code: activity.revenue_code,
          modifier_1: activity.modifier_1,
          modifier_2: activity.modifier_2,
          charge_amount: activity.rate_per_unit
        };

        const diagCodes = await fetchActivityDiagnosisCodes(value);
        if (diagCodes && diagCodes.length > 0) {
          const defaultDiag = diagCodes.find((d: ActivityDiagnosisCode) => d.is_default) || diagCodes[0];
          if (defaultDiag) {
            updated[index].diagnosis_code = defaultDiag.icd10_code;
          }
        }

        if (updated[index].member_id) {
          await validateLineItem(updated, index);
        }
      }
    }

    if (field === 'member_id' && value && updated[index].billing_activity_id) {
      await validateLineItem(updated, index);
    }

    setLineItems(updated);
  };

  // Validation
  const validateLineItem = async (items: BulkClaimLineItem[], index: number) => {
    const item = items[index];
    if (!item.member_id || !item.billing_activity_id) {
      items[index].validation_status = 'pending';
      items[index].validation_messages = [];
      return;
    }

    try {
      const result = await billingDependenciesAPI.validateBillingActivity({
        member_id: item.member_id,
        activity_id: item.billing_activity_id,
        service_date: item.service_date,
        units: item.units
      });

      const messages: string[] = [];
      let status: 'valid' | 'warning' | 'error' = 'valid';

      if (result.violations.length > 0) {
        const blocking = result.violations.filter(v => v.severity === 'blocking' || v.severity === 'error');
        if (blocking.length > 0) {
          status = 'error';
          messages.push(...blocking.map(v => v.message));
        }
      }

      if (result.warnings.length > 0 && status !== 'error') {
        status = 'warning';
        messages.push(...result.warnings.map(w => w.message));
      }

      items[index].validation_status = status;
      items[index].validation_messages = messages;
    } catch (err) {
      console.error('Validation error:', err);
      items[index].validation_status = 'valid';
      items[index].validation_messages = [];
    }
  };

  const validateAllItems = async () => {
    const updated = [...lineItems];
    for (let i = 0; i < updated.length; i++) {
      if (updated[i].member_id && updated[i].billing_activity_id) {
        await validateLineItem(updated, i);
      }
    }
    setLineItems(updated);
  };

  // Keyboard navigation
  const handleKeyDown = (e: KeyboardEvent<HTMLElement>, rowIndex: number, field: string) => {
    const fields = ['member_id', 'billing_activity_id', 'service_date', 'place_of_service', 'diagnosis_code', 'units', 'charge_amount'];
    const currentFieldIndex = fields.indexOf(field);

    switch (e.key) {
      case 'Tab':
        if (!e.shiftKey && currentFieldIndex === fields.length - 1) {
          e.preventDefault();
          if (rowIndex === lineItems.length - 1) {
            addLineItem();
          } else {
            setFocusedCell({ rowIndex: rowIndex + 1, field: fields[0] });
          }
        } else if (e.shiftKey && currentFieldIndex === 0 && rowIndex > 0) {
          e.preventDefault();
          setFocusedCell({ rowIndex: rowIndex - 1, field: fields[fields.length - 1] });
        }
        break;

      case 'Enter':
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          handleSubmitAll();
        } else {
          e.preventDefault();
          if (rowIndex === lineItems.length - 1) {
            addLineItem();
          } else {
            setFocusedCell({ rowIndex: rowIndex + 1, field });
          }
        }
        break;

      case 'd':
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          duplicateLineItem(rowIndex);
        }
        break;

      case 'Delete':
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          if (lineItems.length > 1) {
            removeLineItem(rowIndex);
          }
        }
        break;

      case 's':
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          saveDraft();
          setSuccessMessage('Draft saved');
          setTimeout(() => setSuccessMessage(null), 2000);
        }
        break;
    }
  };

  // Submit all claims
  const handleSubmitAll = async () => {
    const today = new Date().toISOString().split('T')[0];

    const futureDateItems = lineItems.filter(item =>
      item.service_date && item.service_date > today
    );

    if (futureDateItems.length > 0) {
      setError(`${futureDateItems.length} claim(s) have service dates in the future. Service date must be current or prior date.`);
      return;
    }

    const validItems = lineItems.filter(item =>
      item.member_id &&
      item.billing_activity_id &&
      item.service_date &&
      item.service_date <= today &&
      item.validation_status !== 'error'
    );

    if (validItems.length === 0) {
      setError('No valid claims to submit. Please ensure all required fields are filled and dates are not in the future.');
      return;
    }

    if (!payerId) {
      setError('Please select a payer');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const claims = validItems.map(item => ({
        organization_id: organizationId,
        member_id: item.member_id,
        payer_id: payerId,
        program_id: programId,
        billing_provider_npi: billingProviderNpi,
        service_date_from: item.service_date,
        service_date_to: item.service_date,
        place_of_service: item.place_of_service,
        diagnosis_code_1: item.diagnosis_code,
        line_items: [{
          line_number: 1,
          billing_activity_id: item.billing_activity_id,
          cpt_code: item.cpt_code,
          hcpcs_code: item.hcpcs_code,
          revenue_code: item.revenue_code,
          modifier_1: item.modifier_1,
          modifier_2: item.modifier_2,
          service_date: item.service_date,
          units: item.units,
          charge_amount: item.charge_amount
        }]
      }));

      const createdIds: number[] = [];
      const errors: string[] = [];

      for (const claim of claims) {
        try {
          const response = await fetch(getWeaveinApiUrl('/api/claims'), {
            method: 'POST',
            credentials: 'include',
            headers: getAuthHeaders(),
            body: JSON.stringify(claim)
          });

          if (response.ok) {
            const data = await response.json();
            createdIds.push(data.id);
          } else {
            const errorData = await response.json();
            errors.push(errorData.detail || 'Failed to create claim');
          }
        } catch (err: any) {
          errors.push(err.message || 'Network error');
        }
      }

      if (createdIds.length > 0) {
        const submittedMemberIds = validItems.map(item => item.id);
        setLineItems(lineItems.filter(item => !submittedMemberIds.includes(item.id)));

        if (lineItems.length === validItems.length) {
          localStorage.removeItem(STORAGE_KEY);
        }

        setSuccessMessage(`Successfully created ${createdIds.length} claim(s)`);
      }

      if (errors.length > 0) {
        setError(`${errors.length} claim(s) failed: ${errors[0]}`);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to submit claims');
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate totals
  const totalAmount = lineItems.reduce((sum, item) => sum + (item.units * item.charge_amount), 0);
  const validCount = lineItems.filter(item => item.member_id && item.billing_activity_id && item.validation_status !== 'error').length;

  return (
    <div className="max-w-full mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-4 mb-4">
          <Button variant="ghost" size="sm" onClick={() => onNavigate?.('billing-claims')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Claims
          </Button>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {!tabbed && <FileText className="h-8 w-8 text-blue-600" />}
            <div>
              {!tabbed && <h1 className="font-display text-lg font-semibold tracking-[-0.015em] text-ink">Bulk Claim Entry</h1>}
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Building2 className="h-4 w-4" />
                <span>{organizationName || 'Current Organization'}</span>
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowShortcuts(!showShortcuts)}
            title="Keyboard shortcuts"
          >
            <Keyboard className="h-4 w-4 mr-2" />
            Shortcuts
          </Button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <Card className="p-4 mb-4 bg-red-50 border-red-200">
          <div className="flex items-center gap-2 text-red-800">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <p className="flex-1">{error}</p>
            <Button variant="ghost" size="sm" onClick={() => setError(null)}>
              Dismiss
            </Button>
          </div>
        </Card>
      )}

      {successMessage && (
        <Card className="p-4 mb-4 bg-green-50 border-green-200">
          <div className="flex items-center gap-2 text-green-800">
            <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
            <p className="flex-1">{successMessage}</p>
          </div>
        </Card>
      )}

      {/* Keyboard Shortcuts Panel */}
      {showShortcuts && (
        <Card className="p-4 mb-4 bg-blue-50 border-blue-200">
          <h4 className="font-semibold text-blue-900 mb-2">Keyboard Shortcuts</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><kbd className="px-2 py-1 bg-white rounded border">Tab</kbd> Next field</div>
            <div><kbd className="px-2 py-1 bg-white rounded border">Enter</kbd> Next row</div>
            <div><kbd className="px-2 py-1 bg-white rounded border">Ctrl+D</kbd> Duplicate row</div>
            <div><kbd className="px-2 py-1 bg-white rounded border">Ctrl+Del</kbd> Delete row</div>
            <div><kbd className="px-2 py-1 bg-white rounded border">Ctrl+S</kbd> Save draft</div>
            <div><kbd className="px-2 py-1 bg-white rounded border">Ctrl+Enter</kbd> Submit all</div>
          </div>
        </Card>
      )}

      {/* Batch Settings */}
      <Card className="p-4 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <Label>Payer *</Label>
            <select
              className="w-full p-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={payerId || ''}
              onChange={(e) => setPayerId(e.target.value ? parseInt(e.target.value) : null)}
            >
              <option value="">Select Payer...</option>
              {payers.map(payer => (
                <option key={payer.id} value={payer.id}>{payer.payer_name}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Program</Label>
            <select
              className="w-full p-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={programId || ''}
              onChange={(e) => setProgramId(e.target.value ? parseInt(e.target.value) : null)}
            >
              <option value="">Select Program...</option>
              {programs.map(program => (
                <option key={program.id} value={program.id}>{program.name}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Billing Provider NPI</Label>
            <Input
              value={billingProviderNpi}
              onChange={(e) => setBillingProviderNpi(e.target.value)}
              placeholder="10-digit NPI"
              maxLength={10}
              className="text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex items-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setShowImportModal(true); fetchImportableMasterClaims(); }}
              title="Import line items from master claims"
            >
              <Download className="h-4 w-4 mr-1" />
              Import from Master Claims
            </Button>
            <Button variant="outline" size="sm" onClick={clearDraft} title="Clear draft">
              <Trash2 className="h-4 w-4 mr-1" />
              Clear
            </Button>
            <Button variant="outline" size="sm" onClick={validateAllItems} title="Validate all">
              <RefreshCw className="h-4 w-4 mr-1" />
              Validate
            </Button>
          </div>
        </div>
      </Card>

      {/* Data Grid */}
      <Card className="mb-4">
        <div className="overflow-x-auto p-4" ref={gridRef}>
          <table className="w-full text-sm border-collapse">
            <thead className="bg-gray-100 sticky top-0">
              <tr>
                <th className="p-2 border text-left w-8">#</th>
                <th className="p-2 border text-left" style={{ minWidth: '180px' }}>Member *</th>
                <th className="p-2 border text-left" style={{ minWidth: '200px' }}>Activity *</th>
                <th className="p-2 border text-left" style={{ minWidth: '130px' }}>Service Date *</th>
                <th className="p-2 border text-left" style={{ minWidth: '140px' }}>Place of Service</th>
                <th className="p-2 border text-left" style={{ minWidth: '180px' }}>Diagnosis Code</th>
                <th className="p-2 border text-right" style={{ minWidth: '80px' }}>Units</th>
                <th className="p-2 border text-right" style={{ minWidth: '100px' }}>Amount</th>
                <th className="p-2 border text-center" style={{ minWidth: '80px' }}>Status</th>
                <th className="p-2 border text-center" style={{ minWidth: '80px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((item, index) => (
                <tr
                  key={item.id}
                  className={`
                    ${item.validation_status === 'error' ? 'bg-red-50' : ''}
                    ${item.validation_status === 'warning' ? 'bg-yellow-50' : ''}
                    ${item.validation_status === 'valid' ? 'bg-green-50' : ''}
                    hover:bg-gray-50
                  `}
                >
                  <td className="p-2 border text-gray-500">{index + 1}</td>

                  {/* Member */}
                  <td className="p-1 border">
                    <select
                      className="w-full p-1 border rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all"
                      value={item.member_id || ''}
                      onChange={(e) => updateLineItem(index, 'member_id', e.target.value ? parseInt(e.target.value) : null)}
                      onKeyDown={(e) => handleKeyDown(e, index, 'member_id')}
                      autoFocus={focusedCell?.rowIndex === index && focusedCell?.field === 'member_id'}
                    >
                      <option value="">Select member...</option>
                      {members.map(member => (
                        <option key={member.id} value={member.id}>
                          {member.last_name}, {member.first_name} {member.member_number ? `(${member.member_number})` : ''}
                        </option>
                      ))}
                    </select>
                  </td>

                  {/* Activity */}
                  <td className="p-1 border">
                    <select
                      className="w-full p-1 border rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all"
                      value={item.billing_activity_id || ''}
                      onChange={(e) => updateLineItem(index, 'billing_activity_id', e.target.value ? parseInt(e.target.value) : null)}
                      onKeyDown={(e) => handleKeyDown(e, index, 'billing_activity_id')}
                      disabled={!programId}
                    >
                      <option value="">{programId ? 'Select activity...' : 'Select program first'}</option>
                      {billingActivities.map(activity => (
                        <option key={activity.id} value={activity.id}>
                          {activity.activity_name} {activity.cpt_code ? `(${activity.cpt_code})` : ''} - ${activity.rate_per_unit}
                        </option>
                      ))}
                    </select>
                  </td>

                  {/* Service Date */}
                  <td className="p-1 border">
                    <Input
                      type="date"
                      className={`text-sm p-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${item.service_date && item.service_date > new Date().toISOString().split('T')[0] ? 'border-red-500 bg-red-50' : ''}`}
                      value={item.service_date}
                      max={new Date().toISOString().split('T')[0]}
                      onChange={(e) => updateLineItem(index, 'service_date', e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e as any, index, 'service_date')}
                      title="Service date cannot be in the future"
                    />
                  </td>

                  {/* Place of Service */}
                  <td className="p-1 border">
                    <select
                      className={`w-full p-1 border rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all ${programPlaceOfService.length > 0 ? 'bg-blue-50' : ''}`}
                      value={item.place_of_service}
                      onChange={(e) => updateLineItem(index, 'place_of_service', e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, index, 'place_of_service')}
                      title={programPlaceOfService.length > 0 ? 'Program-specific place of service' : ''}
                    >
                      {availablePlaceOfServiceCodes.map(pos => (
                        <option key={pos.value} value={pos.value}>{pos.label}</option>
                      ))}
                    </select>
                  </td>

                  {/* Diagnosis Code */}
                  <td className="p-1 border">
                    {item.billing_activity_id && activityDiagnosisCodes[item.billing_activity_id]?.length > 0 ? (
                      <select
                        className="w-full p-1 border rounded text-sm bg-green-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all"
                        value={item.diagnosis_code || ''}
                        onChange={(e) => updateLineItem(index, 'diagnosis_code', e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, index, 'diagnosis_code')}
                        title="Activity-linked diagnosis codes"
                      >
                        <option value="">Select diagnosis...</option>
                        {activityDiagnosisCodes[item.billing_activity_id].map(diag => (
                          <option key={diag.icd10_code_id} value={diag.icd10_code}>
                            {diag.icd10_code} - {diag.icd10_short_description || diag.icd10_description}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <Input
                        type="text"
                        className="text-sm p-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        value={item.diagnosis_code || ''}
                        onChange={(e) => updateLineItem(index, 'diagnosis_code', e.target.value.toUpperCase())}
                        onKeyDown={(e) => handleKeyDown(e as any, index, 'diagnosis_code')}
                        placeholder={item.billing_activity_id ? 'No linked codes' : 'Select activity first'}
                        maxLength={10}
                      />
                    )}
                  </td>

                  {/* Units */}
                  <td className="p-1 border">
                    <Input
                      type="number"
                      min="0.25"
                      step="0.25"
                      className="text-sm p-1 text-right focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      value={item.units}
                      onChange={(e) => updateLineItem(index, 'units', parseFloat(e.target.value) || 1)}
                      onKeyDown={(e) => handleKeyDown(e as any, index, 'units')}
                    />
                  </td>

                  {/* Amount */}
                  <td className="p-1 border">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      className="text-sm p-1 text-right focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      value={item.charge_amount}
                      onChange={(e) => updateLineItem(index, 'charge_amount', parseFloat(e.target.value) || 0)}
                      onKeyDown={(e) => handleKeyDown(e as any, index, 'charge_amount')}
                    />
                  </td>

                  {/* Status */}
                  <td className="p-1 border text-center">
                    {item.validation_status === 'valid' && (
                      <CheckCircle2 className="h-5 w-5 text-green-600 mx-auto" title="Valid" />
                    )}
                    {item.validation_status === 'warning' && (
                      <div className="relative group">
                        <AlertTriangle className="h-5 w-5 text-yellow-600 mx-auto cursor-help" />
                        {item.validation_messages.length > 0 && (
                          <div className="absolute z-10 hidden group-hover:block bg-yellow-100 border border-yellow-300 rounded p-2 text-xs text-left w-48 -left-20 top-6">
                            {item.validation_messages.map((msg, i) => (
                              <div key={i}>{msg}</div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {item.validation_status === 'error' && (
                      <div className="relative group">
                        <AlertCircle className="h-5 w-5 text-red-600 mx-auto cursor-help" />
                        {item.validation_messages.length > 0 && (
                          <div className="absolute z-10 hidden group-hover:block bg-red-100 border border-red-300 rounded p-2 text-xs text-left w-48 -left-20 top-6">
                            {item.validation_messages.map((msg, i) => (
                              <div key={i}>{msg}</div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {item.validation_status === 'pending' && (
                      <span className="text-gray-400 text-xs">-</span>
                    )}
                  </td>

                  {/* Actions */}
                  <td className="p-1 border text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 hover:bg-blue-100"
                        onClick={() => duplicateLineItem(index)}
                        title="Duplicate (Ctrl+D)"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-100"
                        onClick={() => removeLineItem(index)}
                        title="Delete (Ctrl+Delete)"
                        disabled={lineItems.length === 1}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Add Row Button */}
          <div className="mt-2">
            <Button variant="outline" size="sm" onClick={addLineItem}>
              <Plus className="h-4 w-4 mr-1" />
              Add Row
            </Button>
          </div>
        </div>
      </Card>

      {/* Import from Master Claims Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <Card className="w-full max-w-lg mx-4 p-6 bg-white max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Import from Master Claims</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowImportModal(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Select a master claim to import its line items into the bulk entry grid.
            </p>
            {importLoading ? (
              <div className="text-center py-8 text-gray-500">
                <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2" />
                Loading master claims...
              </div>
            ) : importableClaims.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <FileText className="h-8 w-8 mx-auto mb-2" />
                <p>No unconverted master claims available.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {importableClaims.map((mc) => (
                  <button
                    key={mc.id}
                    onClick={() => handleImportMasterClaim(mc.id)}
                    className="w-full text-left p-3 border rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium text-gray-900">
                          {mc.member_name || `Member #${mc.member_id}`}
                        </span>
                        {mc.health_plan && (
                          <span className="ml-2 text-xs text-gray-500">
                            {mc.health_plan}
                          </span>
                        )}
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        mc.status === 'approved' ? 'bg-green-100 text-green-700' :
                        mc.status === 'ready' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {mc.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      {mc.due_date && <span>Due: {new Date(mc.due_date).toLocaleDateString()}</span>}
                      <span>{mc.total_visits + mc.total_deliveries} line item(s)</span>
                      {mc.submitted_on_portal && (
                        <span className="text-indigo-600">Portal submitted</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Footer */}
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 text-sm">
            <span className="text-gray-600">
              {lineItems.length} row(s) | {validCount} valid
            </span>
            <span className="font-semibold text-lg">
              Total: ${totalAmount.toFixed(2)}
            </span>
            {lineItems.some(item => !item.is_saved) && (
              <Badge variant="outline" className="bg-yellow-100 text-yellow-800">
                Unsaved changes
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => {
                saveDraft();
                setSuccessMessage('Draft saved');
                setTimeout(() => setSuccessMessage(null), 2000);
              }}
            >
              <Save className="h-4 w-4 mr-2" />
              Save Draft
            </Button>
            <Button
              onClick={handleSubmitAll}
              disabled={submitting || validCount === 0 || !payerId}
            >
              {submitting ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Submit {validCount} Claim(s)
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
